// frontend/src/api/index.js
// Updated: April 10, 2025
// Fixed API URL routes to include the required /api prefix

import axios from 'axios';
import { getToken } from '../auth/auth';
import { API_URL } from '../config';

// Import the database API functions
import { 
  getDatabaseLeads, 
  getDatabaseLead, 
  updateDatabaseLead, 
  deleteDatabaseLead,
  createDatabaseLead,
  getDatabaseLeadsStats,
  getDatabaseAgents,
  getDatabaseAgentsByState
} from './database';

// Export the database API functions
export {
  getDatabaseLeads,
  getDatabaseLead,
  updateDatabaseLead,
  deleteDatabaseLead,
  createDatabaseLead,
  getDatabaseLeadsStats,
  getDatabaseAgents,
  getDatabaseAgentsByState
};

// Create axios instance with auth header
const apiClient = axios.create({
  baseURL: `${API_URL}/api`, // Fixed to include the /api prefix
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  timeout: 30000, // 30 second timeout
  validateStatus: function (status) {
    return status >= 200 && status < 500; // Accept all status codes less than 500
  }
});

// Track rate limiting state
let isRateLimited = false;
let rateLimitResetTime = null;

// Add request interceptor for auth token and rate limiting
apiClient.interceptors.request.use(async config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // If we're rate limited, wait until the reset time
  if (isRateLimited && rateLimitResetTime) {
    const now = Date.now();
    if (now < rateLimitResetTime) {
      // Wait for the remaining time
      await new Promise(resolve => setTimeout(resolve, rateLimitResetTime - now));
    }
    isRateLimited = false;
    rateLimitResetTime = null;
  }

  // Ensure credentials are included in the request
  config.withCredentials = true;
  
  // Only add timestamp to non-GET requests to prevent caching issues
  if (config.method !== 'get') {
    config.params = {
      ...config.params,
      _t: new Date().getTime()
    };
  }
  
  return config;
}, error => {
  return Promise.reject(error);
});

// Add response interceptor to handle token expiration, errors, and rate limiting
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      // Handle 401 Unauthorized errors
      if (error.response.status === 401) {
        // Check if this is a Unbounce authentication error
        const isUnbounceError = error.config.url.includes('/unbounce/') && 
          error.response.data?.message?.includes('Unbounce authentication failed');
        
        if (!isUnbounceError) {
          // Only log out for non-Unbounce 401 errors
          console.log("API received 401 Unauthorized response - logging out user");
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/';
        }
      }
      
      // Handle 403 Forbidden errors
      if (error.response.status === 403) {
        console.log("API received 403 Forbidden response");
        // Redirect to dashboard if not admin
        if (window.location.pathname.startsWith('/admin')) {
          window.location.href = '/dashboard';
        }
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Network Error:', error.request);
      // You might want to show a user-friendly error message here
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error:', error.message);
    }

    if (error.response?.status === 429) {
      isRateLimited = true;
      // Get retry-after header or use default 5 seconds
      const retryAfter = parseInt(error.response.headers['retry-after'] || '5', 10);
      rateLimitResetTime = Date.now() + (retryAfter * 1000);
      
      // Return a more informative error
      error.message = `Rate limit exceeded. Please try again in ${retryAfter} seconds.`;
    }
    return Promise.reject(error);
  }
);

// Response handler
const handleResponse = (response) => {
  // If the response contains a 'leads' property, preserve it
  if (response.data.leads) {
    return {
      success: true,
      leads: response.data.leads,
      message: response.data.message
    };
  }
  
  // Otherwise fall back to the default data handling
  return {
    success: true,
    data: response.data.data || response.data,
    message: response.data.message
  };
};

const handleError = (error) => {
  console.error('API Error:', {
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data,
    message: error.message,
    config: {
      url: error.config?.url,
      method: error.config?.method,
      params: error.config?.params
    }
  });
  
  return {
    success: false,
    message: error.response?.data?.message || error.message || 'An error occurred. Please try again.'
  };
};

// Export the configured client and handlers
export { apiClient, handleResponse, handleError };

// Export all functions from leads.js
export * from './leads';

// Export all functions from emailApi.js
export * from './emailApi';

// All other API functions...
// Note: Remove any duplicated lead or email functions from here since they're now imported from their respective files

// Keep only unique functions that aren't in leads.js or emailApi.js
export const getAgents = async () => {
  // Use the database client function instead
  console.log('Using getDatabaseAgents instead of getAgents');
  return getDatabaseAgents();
};

export const getLeads = async (state) => {
  try {
    const response = await apiClient.get('/agent/leads', { params: { state } });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const getLead = async (id) => {
  try {
    const response = await apiClient.get(`/agent/leads/${id}`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const purchaseLead = async (id, paymentMethod) => {
  try {
    const response = await apiClient.post(`/agent/leads/${id}/purchase`, { paymentMethod });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

// Agent-related API calls
export const updateAgentProfile = async (profileData) => {
  try {
    const response = await apiClient.put('/agent/profile', profileData);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const updateEmailPreferences = async (preferences) => {
  try {
    const response = await apiClient.put('/agent/email-preferences', preferences);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const updateSelectedStates = async (selectedStates) => {
  try {
    const response = await apiClient.put('/agent/states', { selectedStates });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const getAgentEmails = async () => {
  try {
    const response = await apiClient.get('/agent/emails');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const addAgentEmail = async (email) => {
  try {
    const response = await apiClient.post('/agent/emails', { email });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

// Admin-specific API calls
export const updateAgent = async (id, agentData) => {
  try {
    const response = await apiClient.put(`/admin/agents/${id}`, agentData);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const createAgent = async (agentData) => {
  try {
    const response = await apiClient.post('/admin/agents', agentData);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const deleteAgent = async (id) => {
  try {
    const response = await apiClient.delete(`/admin/agents/${id}`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const getAdminLeads = async () => {
  try {
    const response = await apiClient.get('/admin/leads');
    console.log("Raw API response:", response);
    return handleResponse(response);
  } catch (error) {
    console.error("API error in getAdminLeads:", error);
    return handleError(error);
  }
};

export const getUnbounceLeads = async (params = {}) => {
  try {
    const response = await apiClient.get('/admin/unbounce/leads', { params });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const getUnbounceLeadsAnalytics = async () => {
  try {
    const response = await apiClient.get('/admin/unbounce/analytics');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const getUnbounceAccounts = async () => {
  try {
    const response = await apiClient.get('/admin/unbounce/accounts');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const getLastSyncTime = async () => {
  try {
    const response = await apiClient.get('/admin/unbounce/last-sync');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const refreshUnbounceToken = async () => {
  try {
    const response = await apiClient.post('/admin/unbounce/refresh-token');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const updateLead = async (id, leadData) => {
  try {
    const response = await apiClient.put(`/admin/leads/${id}`, leadData);
    if (response.data.success) {
      return response.data;
    } else {
      console.error('Update lead failed:', response.data.message);
      return { success: false, message: response.data.message || 'Failed to update lead' };
    }
  } catch (error) {
    console.error('Update lead error:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to update lead'
    };
  }
};

export const createLead = async (leadData) => {
  try {
    const response = await apiClient.post('/admin/leads', leadData);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const deleteLead = async (id) => {
  try {
    const response = await apiClient.delete(`/admin/leads/${id}`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const getSystemSettings = async () => {
  try {
    const response = await apiClient.get('/admin/settings');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const updateSystemSettings = async (settings) => {
  try {
    const response = await apiClient.put('/admin/settings', settings);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const getTransactions = async () => {
  try {
    const response = await apiClient.get('/admin/transactions');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const deleteAgentEmail = async (email) => {
  try {
    const response = await apiClient.delete(`/agent/emails/${encodeURIComponent(email)}`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const getAgentSettings = async () => {
  try {
    const response = await apiClient.get('/agent/settings');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const updateAgentSettings = async (settings) => {
  try {
    const response = await apiClient.put('/agent/settings', settings);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export const getAdminLeadById = async (id) => {
  try {
    console.log("Fetching admin lead by ID:", id);
    console.log("Making request to:", `/admin/leads/${id}`);
    const response = await apiClient.get(`/admin/leads/${id}`);
    console.log("Raw admin lead response:", response.data);
    
    // The backend sends the lead data in response.data.data
    const leadData = response.data.data || response.data;
    console.log("Processed lead data:", {
      id: leadData.id,
      name: leadData.name,
      state: leadData.state,
      city: leadData.city,
      zip_code: leadData.zip_code
    });
    
    return {
      success: true,
      data: leadData,
      message: response.data.message || 'Lead fetched successfully'
    };
  } catch (error) {
    console.error("Error fetching admin lead:", error);
    return handleError(error);
  }
};

export const bulkUploadLeads = async (formData) => {
  try {
    const token = getToken();
    const response = await fetch(`${API_URL}/admin/leads/bulk`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to upload leads');
    }
    
    return await response.json();
  } catch (error) {
    return handleError(error);
  }
};

export const repairLeads = async () => {
  try {
    const response = await apiClient.post('/admin/leads/repair');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};




/**
 * Get city and state from zip code
 * Used to auto-populate location information for leads based on zip code
 * @param {string} zipCode - The zip code to look up
 * @returns {Promise} API response with city and state
 */
export const getLocationFromZip = async (zipCode) => {
  try {
    const response = await apiClient.get(`/utils/location/${zipCode}`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Update lead location data
 * Updates a lead's city and state based on zip code
 * @param {number} leadId - The ID of the lead
 * @returns {Promise} API response with updated lead data
 */
export const updateLeadLocation = async (leadId) => {
  try {
    const response = await apiClient.post(`/admin/leads/${leadId}/update-location`);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Update multiple leads' location data in bulk
 * Updates city and state for all leads based on their zip codes
 * @returns {Promise} API response with summary of updates
 */
export const bulkUpdateLeadLocations = async () => {
  try {
    const response = await apiClient.post('/admin/leads/bulk-update-locations');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};



/**
 * Get preview data for email templates
 * Returns the rendered HTML for different email types with sample data
 * @param {string} emailType - The type of email template ('initial', 'full')
 * @param {number} leadId - Optional lead ID to use real data instead of sample data
 * @returns {Promise} API response with rendered HTML
 */
export const getEmailPreview = async (emailType, leadId = null) => {
  try {
    let url = `/admin/email/preview/${emailType}`;
    if (leadId) url += `?leadId=${leadId}`;
    
    const response = await apiClient.get(url);
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Synchronize leads from Unbounce
 * Triggers the server to pull the latest leads from Unbounce
 * @returns {Promise} API response with sync results
 */
export const synchronizeUnbounceLeads = async () => {
  try {
    const response = await apiClient.post('/admin/unbounce/sync');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};


export const checkLocationService = async () => {
  try {
    // Use the correct API URL
    const response = await axios.get(`https://api.annuityleadsforagents.com/api/utils/location/90210`);
    return {
      success: true,
      status: 'online',
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      status: 'offline',
      error: error.response?.data?.message || error.message
    };
  }
};

/**
 * Synchronize leads from Call Your Client system
 * @returns {Promise} API response with sync results
 */
export const synchronizeCYCLeads = async () => {
  try {
    const response = await apiClient.post('/admin/leads/sync-cyc');
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};